import { useEffect, useState } from 'react';
import { Button } from 'components/Button';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { InfoButton } from 'components/InfoButton';
import { useInfoDrawer } from 'context/InfoDrawerContext';

import { CostTypeCard } from './components/CostTypeCard';
import { ConfigurationFilesEmptyState } from './components/ConfigurationFilesEmptyState';
import { UploadCostConfigModal } from './components/UploadCostConfigModal';
import { CostConfigurationsInfoContent } from './components/CostConfigurationsInfoContent';
import {
  useCostConfigurationCards,
  useCostUploads,
  useDownloadAllCostConfigFiles,
  useDownloadCostConfigFile,
} from 'components/profitability/configQueryHooks';
import { CostUploadTable } from 'pages/Profitability/costConfigurations/components/CostUploadTable';
import { useDownloadProgress } from 'hooks/useDownloadProgress';

export function CostConfigurationsPage() {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const { data: costConfigurationCards } = useCostConfigurationCards(isUploadModalOpen);
  const downloadAllCostConfigFiles = useDownloadAllCostConfigFiles();
  const downloadCostConfigFile = useDownloadCostConfigFile();

  const { withDownloadProgress } = useDownloadProgress();

  const { data: costUploads } = useCostUploads(isUploadModalOpen);

  const queryClient = useQueryClient();

  const handleUploadSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: ['upload-statuses'] });
  };

  const { setContent } = useInfoDrawer();

  useEffect(() => {
    setContent({
      titleKey: 'profitability.cost-configurations.info.title',
      content: <CostConfigurationsInfoContent />,
    });

    return () => {
      setContent(null);
    };
  }, [setContent]);

  return (
    <div className="px-10 max-w-none">
      <PageContainer>
        <PageSectionTitle
          className="col-span-6 xl:col-span-12"
          titleId="profitability.cost-configurations.title"
          subtitle={<FormattedMessage id="profitability.cost-configurations.subtitle" />}
          rightComponent={<InfoButton tooltipText="View page information" showText />}
          testId="cost-configurations-header"
        />

        <div className="@container col-span-6 xl:col-span-12 grid grid-cols-12 gap-4">
          {(costConfigurationCards ?? []).map(card => (
            <div className={'@[1300px]:col-span-3 col-span-6'} key={card.key}>
              <CostTypeCard
                titleId={`profitability.cost-configurations.card.${card.key}.title`}
                status={card.status}
                statusDate={card.statusDate}
                onDownload={() => void withDownloadProgress(() => downloadCostConfigFile(card.key))}
              />
            </div>
          ))}
        </div>

        <hr className="w-full col-span-6 xl:col-span-12 border-t border-snow-400" />

        <div
          className="flex justify-end gap-2 w-full col-span-6 xl:col-span-12"
          data-testid="cost-configurations-toolbar"
        >
          <Button
            priority="secondary"
            iconLeft="Download"
            data-testid="download-current-file-button"
            onClick={() => void withDownloadProgress(() => downloadAllCostConfigFiles())}
          >
            <FormattedMessage id="profitability.cost-configurations.toolbar.downloadCurrentFile" />
          </Button>
          <Button data-testid="upload-new-button" onClick={() => setIsUploadModalOpen(true)}>
            <FormattedMessage id="profitability.cost-configurations.toolbar.uploadNew" />
          </Button>
        </div>

        {!costUploads ||
          (costUploads.length === 0 && (
            <Tile
              titleId="profitability.cost-configurations.configFiles.title"
              testId="cost-configurations-config-files"
            >
              <ConfigurationFilesEmptyState onUploadNow={() => setIsUploadModalOpen(true)} />
            </Tile>
          ))}

        {costUploads && costUploads.length > 0 && (
          <div className="col-span-6 xl:col-span-12 min-w-0">
            <CostUploadTable uploads={costUploads} />
          </div>
        )}
      </PageContainer>

      <UploadCostConfigModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
}
